// Scenario controls. Values left at "default" are sent as null so the engine uses the graded
// register value (and the confidence report shows its grade, not a user star).
import { Link } from 'react-router-dom';
import { RotateCcw, SlidersHorizontal } from 'lucide-react';
import { Field, Panel, Segmented, Slider } from '../../components/ui';
import { pct, usdT, num } from '../../lib/format';
import { overrideCount, useScenario, type RouteMode } from '../../state/scenario';
import type { Meta } from '../../types/api';

export function ScenarioPanel({ meta }: { meta: Meta }) {
  const { state, dispatch } = useScenario();
  const s = state.scenario;
  const d = meta.defaults;
  const patch = (p: Partial<typeof s>) => dispatch({ type: 'patch', patch: p });
  const linked = s.feed_pricing === 'linked';
  const isIndia = meta.locations.find((l) => l.key === s.location)?.country === 'IN';
  const nOverrides = overrideCount(s);
  const linkPct = (s.overrides['market.sc6_linked_pct_of_lioh'] as number | undefined) ?? d.sc6_linked_pct_of_lioh;

  return (
    <Panel
      title={<span className="row" style={{ gap: 8 }}><SlidersHorizontal size={16} aria-hidden />Scenario</span>}
      action={<button type="button" className="button ghost sm" onClick={() => dispatch({ type: 'reset' })} title="Reset all inputs to register defaults"><RotateCcw size={13} aria-hidden />Reset</button>}
    >
      <div className="stack">
        <p className="section-label">Market</p>
        <Slider label="LiOH benchmark price" value={s.lioh_price_usd_t ?? d.lioh_price_usd_t} min={6000} max={40000} step={250}
          format={usdT} onChange={(v) => patch({ lioh_price_usd_t: v === d.lioh_price_usd_t ? null : v })} />
        {linked ? (
          <Slider label="SC6 as share of LiOH" value={linkPct} min={0.03} max={0.14} step={0.0025} format={(v) => pct(v, 2)}
            hint={`≈ ${usdT(linkPct * (s.lioh_price_usd_t ?? d.lioh_price_usd_t))}`}
            onChange={(v) => (v === d.sc6_linked_pct_of_lioh
              ? dispatch({ type: 'clearOverride', path: 'market.sc6_linked_pct_of_lioh' })
              : dispatch({ type: 'setOverride', path: 'market.sc6_linked_pct_of_lioh', value: v }))} />
        ) : (
          <Slider label="SC6 concentrate price (FOB)" value={s.sc6_fob_usd_t ?? d.sc6_fob_usd_t} min={300} max={3000} step={25}
            format={usdT} onChange={(v) => patch({ sc6_fob_usd_t: v === d.sc6_fob_usd_t ? null : v })} />
        )}
        <Field label="Feed pricing" hint={linked ? 'SC6 follows the hydroxide price (formula-priced offtake).' : 'SC6 price set independently.'}>
          <Segmented label="Feed pricing" value={s.feed_pricing} onChange={(v) => patch({ feed_pricing: v, sc6_fob_usd_t: null })}
            options={[{ value: 'fixed', label: 'Fixed' }, { value: 'linked', label: 'Linked to LiOH' }]} />
        </Field>
        <Field label="Revenue price basis" hint={s.price_basis === 'import_parity' ? 'Indian sites earn the landed import price (freight + MFN duty).' : 'All sites earn the CIF Asia benchmark.'}>
          <Segmented label="Price basis" value={s.price_basis} onChange={(v) => patch({ price_basis: v })}
            options={[{ value: 'benchmark', label: 'Benchmark' }, { value: 'import_parity', label: 'Import parity' }]} />
        </Field>

        <p className="section-label">Project</p>
        <Slider label="Capacity" value={s.capacity_tpa} min={10000} max={60000} step={1000} format={(v) => `${num(v / 1000)} kt/yr`}
          onChange={(v) => patch({ capacity_tpa: v })} />
        <Slider label="Discount rate (WACC, real)" value={s.wacc ?? d.wacc} min={0.06} max={0.16} step={0.005} format={(v) => pct(v)}
          onChange={(v) => patch({ wacc: Math.abs(v - d.wacc) < 1e-9 ? null : v })} />
        <Field label="Process route">
          <select value={state.routeMode} onChange={(e) => dispatch({ type: 'routeMode', mode: e.target.value as RouteMode })}>
            {meta.routes.map((r) => <option key={r.key} value={r.key}>{r.name}{r.illustrative ? ' (illustrative)' : ''}</option>)}
            <option value="both">Compare both routes</option>
          </select>
        </Field>
        <Field label="Ramp-up case" hint={s.ramp_case === 'first_of_kind' ? 'Slower ramp incl. 12–24 month cell-maker qualification.' : 'Recent-plant ramp: 55% → 85% → 100%.'}>
          <Segmented label="Ramp case" value={s.ramp_case} onChange={(v) => patch({ ramp_case: v })}
            options={[{ value: 'base', label: 'Base' }, { value: 'first_of_kind', label: 'First of kind' }]} />
        </Field>
        <label className="toggle"><span>Apply Australia–India ECTA duties</span>
          <input type="checkbox" checked={s.ecta} onChange={(e) => patch({ ecta: e.target.checked })} />
        </label>
        <Slider label="Debt share of capex" value={s.debt_share ?? d.debt_share} min={0} max={0.8} step={0.05} format={(v) => (v === 0 ? 'Unlevered' : pct(v, 0))}
          onChange={(v) => patch({ debt_share: v === d.debt_share ? null : v })} hint="equity IRR + DSCR" />

        <p className="section-label">Focus location</p>
        <Field label="Detail and analytics for" hint={isIndia ? undefined : 'Power priced in AUD for this site.'}>
          <select value={s.location} onChange={(e) => patch({ location: e.target.value })}>
            {meta.locations.map((l) => <option key={l.key} value={l.key}>{l.name}</option>)}
          </select>
        </Field>

        <div className="row-between small" style={{ borderTop: '1px solid var(--line-2)', paddingTop: 12 }}>
          <span className="muted">{nOverrides ? `${nOverrides} assumption override${nOverrides > 1 ? 's' : ''} active` : 'No assumption overrides'}</span>
          <Link className="button ghost sm" to="/assumptions">Edit assumptions</Link>
        </div>
      </div>
    </Panel>
  );
}
